import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  ReactNode,
} from 'react';
import { CheckCircle2, Info, TriangleAlert, X, XCircle } from 'lucide-react';
import { AlertDialog, ConfirmDialog } from '@packages/ui';
import { useTranslation } from 'react-i18next';

interface DialogContextType {
  alert: (message: string, title?: string, type?: 'success' | 'danger' | 'warning' | 'info') => Promise<void>;
  confirm: (message: string, title?: string, type?: 'warning' | 'danger') => Promise<boolean>;
  toast: (message: string, type?: ToastType, durationMs?: number) => void;
}

type ToastType = 'success' | 'danger' | 'warning' | 'info';

interface ToastItem {
  id: number;
  message: string;
  type: ToastType;
}

const DialogContext = createContext<DialogContextType | null>(null);

export const DialogProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { t } = useTranslation('common');
  const translationRef = useRef(t);
  const toastIdRef = useRef(0);
  const toastTimersRef = useRef<Map<number, number>>(new Map());
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const [alertState, setAlertState] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: 'success' | 'danger' | 'warning' | 'info';
    resolve?: () => void;
  }>({ isOpen: false, title: '', message: '', type: 'info' });

  const [confirmState, setConfirmState] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: 'warning' | 'danger';
    resolve?: (value: boolean) => void;
  }>({ isOpen: false, title: '', message: '', type: 'warning' });

  useEffect(() => {
    translationRef.current = t;
  }, [t]);

  const alert = useCallback(
    (
      message: string,
      title?: string,
      type: 'success' | 'danger' | 'warning' | 'info' = 'info',
    ): Promise<void> => {
      return new Promise((resolve) => {
        setAlertState({
          isOpen: true,
          title: title ?? translationRef.current('ui.dialog.alertTitle'),
          message,
          type,
          resolve,
        });
      });
    },
    [],
  );

  const confirm = useCallback(
    (
      message: string,
      title?: string,
      type: 'warning' | 'danger' = 'warning',
    ): Promise<boolean> => {
      return new Promise((resolve) => {
        setConfirmState({
          isOpen: true,
          title: title ?? translationRef.current('ui.dialog.confirmTitle'),
          message,
          type,
          resolve,
        });
      });
    },
    [],
  );

  const dismissToast = useCallback((id: number) => {
    const timer = toastTimersRef.current.get(id);
    if (timer !== undefined) window.clearTimeout(timer);
    toastTimersRef.current.delete(id);
    setToasts((current) => current.filter((item) => item.id !== id));
  }, []);

  const toast = useCallback(
    (message: string, type: ToastType = 'success', durationMs = 3200) => {
      const id = ++toastIdRef.current;
      setToasts((current) => [...current.slice(-2), { id, message, type }]);
      const timer = window.setTimeout(() => {
        toastTimersRef.current.delete(id);
        setToasts((current) => current.filter((item) => item.id !== id));
      }, durationMs);
      toastTimersRef.current.set(id, timer);
    },
    [],
  );

  useEffect(() => () => {
    toastTimersRef.current.forEach((timer) => window.clearTimeout(timer));
    toastTimersRef.current.clear();
  }, []);

  const handleAlertClose = () => {
    alertState.resolve?.();
    setAlertState({ isOpen: false, title: '', message: '', type: 'info' });
  };

  const handleConfirmClose = (result: boolean) => {
    confirmState.resolve?.(result);
    setConfirmState({ isOpen: false, title: '', message: '', type: 'warning' });
  };

  const value = useMemo(() => ({ alert, confirm, toast }), [alert, confirm, toast]);

  return (
    <DialogContext.Provider value={value}>
      {children}
      
      <AlertDialog
        isOpen={alertState.isOpen}
        title={alertState.title}
        message={alertState.message}
        type={alertState.type}
        onConfirm={handleAlertClose}
      />
      
      <ConfirmDialog
        isOpen={confirmState.isOpen}
        title={confirmState.title}
        message={confirmState.message}
        type={confirmState.type}
        onConfirm={() => handleConfirmClose(true)}
        onCancel={() => handleConfirmClose(false)}
      />

      <div
        className="pointer-events-none fixed right-4 top-4 z-[70] flex w-[min(24rem,calc(100vw-2rem))] flex-col gap-2"
        aria-live="polite"
        aria-atomic="false"
      >
        {toasts.map((item) => (
          <ToastMessage key={item.id} item={item} onClose={() => dismissToast(item.id)} />
        ))}
      </div>
    </DialogContext.Provider>
  );
};

const ToastMessage: React.FC<{
  item: ToastItem;
  onClose: () => void;
}> = ({ item, onClose }) => {
  const { t } = useTranslation('common');
  const config = {
    success: { Icon: CheckCircle2, iconClass: 'text-success' },
    danger: { Icon: XCircle, iconClass: 'text-danger' },
    warning: { Icon: TriangleAlert, iconClass: 'text-warning' },
    info: { Icon: Info, iconClass: 'text-accent-blue' },
  }[item.type];
  const Icon = config.Icon;

  return (
    <div
      role={item.type === 'danger' ? 'alert' : 'status'}
      className="pointer-events-auto flex items-start gap-3 rounded-[10px] border border-border-default bg-bg-surface px-3.5 py-3 text-text-primary shadow-lg"
    >
      <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${config.iconClass}`} />
      <p className="min-w-0 flex-1 text-[12.5px] leading-5">{item.message}</p>
      <button
        type="button"
        onClick={onClose}
        aria-label={t('ui.dialog.closeToast')}
        className="flex h-6 w-6 shrink-0 items-center justify-center rounded-[6px] text-text-muted transition-colors hover:bg-bg-elevated hover:text-text-primary"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
};

export const useDialog = (): DialogContextType => {
  const context = useContext(DialogContext);
  if (!context) {
    throw new Error('useDialog must be used within a DialogProvider');
  }
  return context;
};
